import type { ReactNode } from 'react';

export interface PageHeaderProps {
  /** The page title. Plain text — for richer headers (custom typography
   * or icons next to the title) pass a ReactNode. */
  title: ReactNode;
  /** Right-aligned action area — typically one or two `<IconButton>`s
   * (e.g. "+" to create, "search", "more"). Sits opposite the title. */
  actions?: ReactNode;
  /** Optional second row that lives below the title bar — usually a
   * `<PillTabs>`, filter chip row, or breadcrumb. Drawn at the same
   * left padding as the title for visual alignment. */
  children?: ReactNode;
}

/**
 * Header for tab-root pages — Conversations, Tasks, Feed, Discover,
 * My Agent. NOT for detail pages with a back arrow — those use
 * `<DetailHeader>`.
 *
 * Layout:
 *
 *     ┌──────────────────────────────────────────┐
 *     │  {title}                       {actions} │  ← title bar (h-12)
 *     ├──────────────────────────────────────────┤
 *     │  {children}                              │  ← optional row
 *     └──────────────────────────────────────────┘
 *                  hairline border
 *
 * Padding, font weight, hairline divider are all encoded here so the
 * page doesn't have to think about them.
 *
 * @example
 * <PageHeader
 *   title={t('conversations.title')}
 *   actions={<IconButton icon="group_add" onClick={openCreate} />}
 * >
 *   <PillTabs value={mode} onChange={setMode} options={[...]} />
 * </PageHeader>
 */
export function PageHeader({ title, actions, children }: PageHeaderProps) {
  return (
    <div className="border-b border-gray-100 dark:border-white/[0.06]">
      <div className="flex items-center gap-3 px-4 h-12">
        <h1 className="flex-1 min-w-0 text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
          {title}
        </h1>
        {actions && (
          <div className="flex-shrink-0 flex items-center gap-1">
            {actions}
          </div>
        )}
      </div>
      {children && (
        <div className="px-4 pb-2">
          {children}
        </div>
      )}
    </div>
  );
}
